import React, { Fragment, useEffect, useState } from "react";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import { Col, Form, Input, Row, Select, DatePicker, Space, Checkbox, Flex } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import Container from "@/components/container/Container";
import CardComponent from "@/components/card";
import ButtonComponent from "@/components/button/Button";

import { updateAddress } from "@/services/users/updateAddress";
import { updateEmergencyContact } from "@/services/users/updateEmergencyContact";
import { getUserAddress } from "@/services/users/getUserAddress";
import { getEmergencyContact } from "@/services/users/getEmergencyContact";
import { addUserIdentity } from "@/services/users/addUserIdentity";
import { getUserIdentity } from "@/services/users/getUserIdentity";
import { updateUserIdentity } from "@/services/users/updateUserIdentity";
import { addUserAddress } from "@/services/users/addUserAddress";

import { getAllCountries } from "@/services/common/getAllCountries";
import { countryList } from "@/helpers/countryList";

import "@/styles/pages/profile/advance-profile-information.scss";
import BackWithLabel from "@/components/common/BackWithLabel";
import { updateMiscExam } from "@/services/users/updateMiscExam";
import { getMiscExam } from "@/services/users/getMiscExam";
import useTriggerNotification from "@/hooks/useTriggerNotification";
import { getAllCountriesDetails } from "@/services/locations/getAllCountriesDetails";
import { dateFormatList, keyFilterOption } from "@/helpers";
import { getStateByCountry } from "@/services/locations/getStateByCountry";
import { getCityByStateAndCountry } from "@/services/locations/getCityByStateAndCountry";
import getCountryISO3 from "@/lib/data/isoTwoToThree";
import getCountryISO2 from "@/lib/data/isoThreeToTwo";

const stateOptions = [
  {
    value: "US",
    label: "United State of America",
  },
  {
    value: "IN",
    label: "India",
  },
  {
    value: "AU",
    label: "Australia",
  },
];

const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span style={{ color: "red" }}>*</span>}
  </>
);

const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const { Option } = Select;

const AdvaceProfileInformation = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { trigger, element } = useTriggerNotification();

  const [country, setCountry] = useState([]);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [permanentState, setPermanentState] = useState([]);
  const [currentState, setCurrentState] = useState([]);
  const [permanentCity, setPermanentCity] = useState([]);
  const [currentCity, setCurrentCity] = useState([]);
  const [selectedPermanentCountry, setSelectedPermanentCountry] = useState("");
  const [selectedCurrentCountry, setSelectedCurrentCountry] = useState("");

  const [languageExamTaken, setLanguageExamTaken] = useState(false);
  const [standardizedExamTaken, setStandardizedExamTaken] = useState(false);

  const relationshipOptions = [
    {
      value: "Parent",
      label: "Parent",
    },
    {
      value: "Spouse",
      label: "Spouse",
    },
    {
      value: "Sibling",
      label: "Sibling",
    },
    {
      value: "Child",
      label: "Child",
    },
    {
      value: "Guardian",
      label: "Guardian",
    },
    {
      value: "Relative",
      label: "Relative",
    },
    {
      value: "Friend",
      label: "Friend",
    },
  ];

  useEffect(() => {
    fetchCountryData();
  }, []);

  const fetchCountryData = () => {
    getAllCountries()
      .then((response) => {
        setCountry(
          response?.map((x) => ({
            ...x,
            flag:
              x.code !== "0"
                ? `https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/${getCountryISO2(
                    x.code
                  )}.svg`
                : "/assets/images/otherFlag.png",
          }))
        );
      })
      .catch((error) => console.log(error));
  };

  const handleCountryChange = async (code, type) => {
    const response = await getStateByCountry(code);
    if (type === "permanent") {
      setSelectedPermanentCountry(code);
      setPermanentState(response);
      setPermanentCity([]);
      form.resetFields(["state", "city"]);
    } else {
      setSelectedCurrentCountry(code);
      setCurrentState(response);
      setCurrentCity([]);
      form.resetFields(["currentState", "currentCity"]);
    }
  };

  const handleStateChange = async (code, type) => {
    if (type === "permanent") {
      const response = await getCityByStateAndCountry(selectedPermanentCountry, code);
      setPermanentCity(response);
    } else {
      const response = await getCityByStateAndCountry(selectedCurrentCountry, code);
      setCurrentCity(response);
    }
  };
  const handleFinish = (value) => {
    if (!!value.identityId) {
      updateUserIdentity(value.identityId, {
        nameInDocument: value.passportName,
        issuingLocation: value.passportIssueLocation
          ? getCountryISO3(value.passportIssueLocation)
          : "",
        documentNo: value.passportNumber,
        issuedDate: value["passportIssueDate"].format("DD-MM-YYYY"),
        expiryDate: value["passportExpiryDate"].format("DD-MM-YYYY"),
      });
    } else {
      addUserIdentity({
        nameInDocument: value.passportName,
        issuingLocation: value.passportIssueLocation
          ? getCountryISO3(value.passportIssueLocation)
          : "",
        documentNo: value.passportNumber,
        issuedDate: value["passportIssueDate"].format("DD-MM-YYYY"),
        expiryDate: value["passportExpiryDate"].format("DD-MM-YYYY"),
      });
    }
    console.log("value.permanentAddressID ::", !!value.permanentAddressID);
    console.log(value);
    if (!!value.permanentAddressID) {
      updateAddress(value.permanentAddressID, {
        address1: value.addressOne,
        address2: value.addressTwo,
        countryCode: value.country ? getCountryISO3(value.country) : "",
        state: value.state ?? "",
        postCode: value.postCode,
        city: value.city ?? "",
        tag: "Permanent",
      }).then((res) => console.log(res));
    } else {
      addUserAddress({
        address1: value.addressOne,
        address2: value.addressTwo,
        countryCode: value.country ? getCountryISO3(value.country) : "",
        state: value.state ?? "",
        postCode: value.postCode,
        city: value.city ?? "",
        tag: "Permanent",
      }).then((res) => console.log(res));
    }

    console.log("value.currentAddressID :: ", !!value.currentAddressID);

    if (!!value.currentAddressID) {
      updateAddress(value.currentAddressID, {
        address1: value.currentAddress1,
        address2: value.currentAddress2,
        countryCode: value.currentCountry ? getCountryISO3(value.currentCountry) : "",
        state: value.currentState ?? "",
        postCode: value.currentpostCode,
        city: value.currentCity ?? "",
        tag: "Current",
      }).then((res) => console.log(res));
    } else {
      console.log("Calling");
      addUserAddress({
        address1: value.currentAddress1,
        address2: value.currentAddress2,
        countryCode: value.currentCountry ? getCountryISO3(value.currentCountry) : "",
        state: value.currentState ?? "",
        postCode: value.currentpostCode,
        city: value.currentCity ?? "",
        tag: "Current",
      }).then((res) => console.log(res));
    }

    updateEmergencyContact({
      name: value.name,
      mobileNumber: (value.countryCode ? value.countryCode + " " : "+91 ") + value.number,
      email: value.email,
      relation: value.relationship,
    })
      .then(() => {})
      .catch((error) => {
        console.log(error);
      });

    updateMiscExam({
      languageExamTaken: languageExamTaken,
      standardizedExamTaken: standardizedExamTaken,
    })
      .then((res) => {
        console.log(res, "exam updated");
      })
      .catch((error) => {
        console.log(error);
      });

    setIsFormDisabled(true);
    trigger({
      type: "success",
      message: "Profile Information updated successfully",
    });
  };

  const handleCopyAddress = () => {
    setCurrentState([...permanentState]);
    setCurrentCity([...permanentCity]);
    const data = form.getFieldsValue([
      "addressOne",
      "addressTwo",
      "country",
      "state",
      "postCode",
      "city",
    ]);
    if (data.country) {
      setSelectedCurrentCountry(data.country);
      getStateByCountry(data.country)
        .then((response) => setCurrentState(response))
        .catch((err) => console.log(err));
    }
    if (data.state) {
      getCityByStateAndCountry(data.country, data.state)
        .then((res) => setCurrentCity(res))
        .catch((err) => console.log(err));
    }
    form.setFieldsValue({
      currentAddress1: data.addressOne,
      currentAddress2: data.addressTwo,
      currentCountry: data.country ?? "",
      currentState: data.state ?? "",
      currentpostCode: data.postCode,
      currentCity: data.city ?? "",
    });
  };

  useEffect(() => {
    getUserAddress()
      .then((res) => {
        console.log(res);
        const addressData = res.rows;
        const permanentAddress = addressData.find((x) => x.tag === "permanent");
        const currentAddress = addressData.find((x) => x.tag === "current");
        if (permanentAddress) {
          form.setFieldsValue({
            addressOne: permanentAddress.address1,
            addressTwo: permanentAddress.address2,
            country: permanentAddress.countryCode
              ? getCountryISO2(permanentAddress.countryCode)
              : "",
            state: permanentAddress.state ?? "",
            postCode: permanentAddress.postCode,
            city: permanentAddress.city ?? "",
            permanentAddressID: permanentAddress.id,
          });
        }
        if (currentAddress) {
          form.setFieldsValue({
            currentAddress1: currentAddress.address1,
            currentAddress2: currentAddress.address2,
            currentCountry: currentAddress.countryCode
              ? getCountryISO2(currentAddress.countryCode)
              : "",
            currentState: currentAddress.state ?? "",
            currentpostCode: currentAddress.postCode,
            currentCity: currentAddress.city ?? "",
            currentAddressID: currentAddress.id,
          });
        }
      })
      .catch((error) => {
        console.log(error);
      });
    getEmergencyContact()
      .then((res) => {
        console.log(res);
        const number = res.mobileNumber.split(" ");
        form.setFieldsValue({
          name: res.name,
          countryCode: number[0],
          number: number[1],
          email: res.email,
          relationship: res.relation,
        });
      })
      .catch((error) => {
        console.log(error);
      });
    getUserIdentity()
      .then((res) => {
        console.log(res);
        form.setFieldsValue({
          passportName: res[0]?.nameInDocument ?? "",
          passportIssueLocation: res[0]?.issuingLocation
            ? getCountryISO2(res[0]?.issuingLocation)
            : null,
          passportNumber: res[0]?.documentNo ?? "",
          passportIssueDate: res[0]?.issuedDate ? dayjs(res[0]?.issuedDate, dateFormatList) : "",
          passportExpiryDate: res[0]?.expiryDate ? dayjs(res[0]?.expiryDate, dateFormatList) : "",
          identityId: res[0]?.id,
        });
      })
      .catch((err) => console.log(err));

    getMiscExam()
      .then((res) => {
        setLanguageExamTaken(res?.languageExamTaken);
        setStandardizedExamTaken(res?.standardizedExamTaken);
        console.log(res, "exam");
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const disableAfterToday = (current) => {
    return current && current > dayjs().endOf("day");
  };

  const disableBeforeIssueDate = (current) => {
    const issueDate = form.getFieldValue("passportIssueDate");
    return current && issueDate && current < dayjs(issueDate).startOf("day");
  };

  const handleIssueDateChange = () => {
    const issueDate = form.getFieldValue("passportIssueDate");
    const expiryDate = form.getFieldValue("passportExpiryDate");

    if (expiryDate && issueDate && dayjs(issueDate).isAfter(expiryDate)) {
      form.setFieldsValue({ passportExpiryDate: null });
    }
  };

  const handleExpiryDateChange = () => {
    const issueDate = form.getFieldValue("passportIssueDate");
    const expiryDate = form.getFieldValue("passportExpiryDate");

    if (expiryDate && issueDate && dayjs(expiryDate).isBefore(issueDate)) {
      form.setFieldsValue({ passportIssueDate: null });
    }
  };

  useEffect(() => {
    getAllCountriesDetails().then((res) => setCountryData(res));
  }, []);

  useUnsavedChanges(!isFormDisabled);

  return (
    <Fragment>
      {element}
      <div className="advance-contact-information-page">
        <Container>
          <CardComponent>
            <Row>
              <Col xxl={24}>
                <div className="heading">
                  <BackWithLabel
                    label="Edit Advance Profile Information"
                    path="/profile/basic-contact-information"
                  />
                </div>
              </Col>
            </Row>

            <Form
              name="searchGuide"
              initialValues={{ remember: true }}
              layout="vertical"
              onFinish={handleFinish}
              form={form}
              requiredMark={customizeRequiredMark}
            >
              <Row gutter={[16, 0]} justify="start" className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item name="identityId" hidden>
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item
                    label="Name as Appears in Passport"
                    name="passportName"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your passport name!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your passport name"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (!/^[A-Za-z\s]+$/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Passport Issue Location"
                    name="passportIssueLocation"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your passport issue location!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select your passport issue location"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {countryData.map((country) => (
                        <Option key={country.name} value={country.iso2} label={country.name}>
                          <div className="flex justify-center gap-5">{country.name}</div>
                        </Option>
                      ))}
                    </Select>
                    {/* <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your passport issue location"
                    /> */}
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Passport Number"
                    name="passportNumber"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your passport number!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your passport number"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (![...pasteData].every((char) => /[A-Za-z0-9\s]/.test(char))) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Passport Issue Date"
                    name="passportIssueDate"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your passport issue date!",
                      },
                    ]}
                  >
                    <DatePicker
                      disabled={isFormDisabled}
                      placeholder="Select your passport issue date"
                      disabledDate={disableAfterToday}
                      onChange={handleIssueDateChange}
                      format={dateFormatList}
                    />
                  </Form.Item>
                </Col>

                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Passport Expiry Date"
                    name="passportExpiryDate"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your passport expiry date!",
                      },
                    ]}
                  >
                    <DatePicker
                      disabled={isFormDisabled}
                      placeholder="Select your passport expiry date"
                      disabledDate={disableBeforeIssueDate}
                      onChange={handleExpiryDateChange}
                      format={dateFormatList}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="start">
                <Col xxl={24}>
                  <div className="heading">
                    <div className="heading-text title-h6-1">Permanent Address</div>
                  </div>
                </Col>
              </Row>

              <Row gutter={[16, 0]} justify="start" className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item name="permanentAddressID" hidden>
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item
                    label="Address 1"
                    name="addressOne"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your address!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your address"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9,()\-\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (!/^[A-Za-z0-9,()\-\s]+$/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Address 2" name="addressTwo">
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your address"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9,()\-\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (!/^[A-Za-z0-9,()\-\s]+$/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Country"
                    name="country"
                    rules={[
                      {
                        required: true,
                        message: "Please select Preferred Country",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select a country"
                      className="w-100"
                      onChange={(value) => handleCountryChange(value, "permanent")}
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {countryData.map((country) => {
                        return (
                          <Option key={country.name} value={country.iso2} label={country.name}>
                            <div className="flex justify-center gap-5">{country.name}</div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="State/Territory"
                    name="state"
                    rules={[
                      {
                        required: true,
                        message: "Please select state or territory",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select a state/territory"
                      className="w-100"
                      onChange={(value) => handleStateChange(value, "permanent")}
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {permanentState?.map((state) => {
                        return (
                          <Option key={state.name} value={state.iso2} label={state.name}>
                            <div>{state.name}</div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="City"
                    name="city"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your city!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select your city"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {permanentCity.map((city) => {
                        return (
                          <Option key={city.name} value={city.iso2} label={city.name}>
                            <div>{city.name}</div>
                          </Option>
                        );
                      })}
                    </Select>
                    {/* <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your city"
                    /> */}
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Post Code"
                    name="postCode"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your post code!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your post code"
                      onKeyPress={(event) => {
                        if (!/[0-9]/.test(event.key)) {
                          event.preventDefault();
                        }
                      }}
                      onPaste={(event) => {
                        const pasteData = event.clipboardData.getData("text");
                        if (/[^0-9]/.test(pasteData)) {
                          event.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="start">
                <Col xxl={24}>
                  <div className="heading current-address">
                    <div className="heading-text title-h6-1">Current Address</div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item name="isSame" valuePropName="checked">
                    <Checkbox
                      disabled={isFormDisabled}
                      onClick={(event) => event.target.checked && handleCopyAddress()}
                    >
                      Same as permanent address.
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]} justify="start" className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item name="currentAddressID" hidden>
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item
                    label="Address 1"
                    name="currentAddress1"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your address!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your address"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9,()\-\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (!/^[A-Za-z0-9,()\-\s]+$/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Address 2" name="currentAddress2">
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your address"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9,()\-\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (!/^[A-Za-z0-9,()\-\s]+$/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Country"
                    name="currentCountry"
                    rules={[
                      {
                        required: true,
                        message: "Please select Preferred Country",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select a country"
                      className="w-100"
                      onChange={(value) => handleCountryChange(value, "current")}
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {countryData?.map((country) => {
                        return (
                          <Option key={country.name} value={country.iso2} label={country.name}>
                            <div className="flex justify-center gap-5">{country.name}</div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="State/Territory"
                    name="currentState"
                    rules={[
                      {
                        required: true,
                        message: "Please select state or territory",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select a state/territory"
                      className="w-100"
                      onChange={(value) => handleStateChange(value, "current")}
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {currentState.map((state) => {
                        return (
                          <Option key={state.name} value={state.iso2} label={state.name}>
                            <div>{state.name}</div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="City"
                    name="currentCity"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your city!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select your city"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {currentCity.map((city) => {
                        return (
                          <Option key={city.name} value={city.iso2} label={city.name}>
                            <div>{city.name}</div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Post Code"
                    name="currentpostCode"
                    className=""
                    rules={[
                      {
                        required: true,
                        message: "Please enter your post code!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your post code"
                      onKeyPress={(event) => {
                        if (!/[0-9]/.test(event.key)) {
                          event.preventDefault();
                        }
                      }}
                      onPaste={(event) => {
                        const pasteData = event.clipboardData.getData("text");
                        if (/[^0-9]/.test(pasteData)) {
                          event.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="start">
                <Col xxl={24}>
                  <div className="heading">
                    <div className="heading-text title-h6-1">Emergency Contact Details</div>
                  </div>
                </Col>
              </Row>

              <Row gutter={[16, 0]} justify="start" className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your relative name!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your relative name"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (/[^A-Za-z\s]/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Phone Number">
                    <Space.Compact style={{ width: "100%" }}>
                      <Form.Item name={"countryCode"} noStyle>
                        <Select
                          virtual={false}
                          showSearch
                          placeholder="Select Contry"
                          defaultValue={"+91"}
                          filterOption={keyFilterOption}
                          disabled={isFormDisabled}
                          style={{
                            maxWidth: "35%",
                          }}
                        >
                          {countryList.map((country) => {
                            return (
                              <Option
                                key={country.name}
                                value={country.phoneCode}
                                label={country.phoneCode}
                              >
                                <Flex justify="start" align="center" gap={"5px"}>
                                  <img
                                    src={country.flag}
                                    height={30}
                                    width={30}
                                    className={isFormDisabled && "disabled-image"}
                                  />
                                  {window.innerWidth > 425 ? (
                                    <span>{country.phoneCode}</span>
                                  ) : (
                                    <></>
                                  )}
                                </Flex>
                              </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name="number"
                        noStyle
                        rules={[
                          {
                            required: true,
                            message: "Please enter a valid WhatsApp registered number.",
                            pattern: new RegExp(/^([0-9]{7,14})$/),
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          style={{
                            width: "80%",
                          }}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          disabled={isFormDisabled}
                          placeholder="Enter your mobile number"
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>
                    </Space.Compact>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Email"
                    name="email"
                    className=""
                    rules={[
                      {
                        type: "email",
                        required: true,
                        message: "Please enter your relative email!",
                      },
                    ]}
                  >
                    <Input disabled={isFormDisabled} placeholder="Enter your relative email" />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Relationship"
                    name="relationship"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your relationship with that relative!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      placeholder="Select a relationship with referee"
                      className="w-100"
                      // onChange={(value) => handleChange(value, "gender")}
                      // filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {relationshipOptions.map((relation) => {
                        return (
                          <Option
                            key={relation.value}
                            value={relation.value}
                            label={relation.label}
                          >
                            {relation.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <div className="exams">
                <Row gutter={[16, 16]} justify="start">
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <div className="advance-question">
                      <CardComponent>
                        <div className="heading body-b1">
                          English language & Standardize Exam Scores
                        </div>
                        <div className="question body-b3">
                          Have you appeared for any English language test in the last two year?
                        </div>
                        <div className="anster-button">
                          <Row gutter={[16, 16]} justify="center">
                            <Col xl={12} md={24} sm={24} xs={24} style={{ maxHeight: "20px" }}>
                              <Form.Item>
                                <Checkbox
                                  value={false}
                                  checked={languageExamTaken === true}
                                  disabled={isFormDisabled}
                                  onChange={() => setLanguageExamTaken(true)}
                                >
                                  Yes
                                </Checkbox>
                              </Form.Item>
                            </Col>
                            <Col xl={12} md={24} sm={24} xs={24} style={{ maxHeight: "20px" }}>
                              <Form.Item>
                                <Checkbox
                                  value={false}
                                  checked={languageExamTaken === false}
                                  disabled={isFormDisabled}
                                  onChange={() => setLanguageExamTaken(false)}
                                >
                                  No
                                </Checkbox>
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      </CardComponent>
                    </div>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <div className="advance-question">
                      <CardComponent>
                        <div className="heading body-b1">
                          Other language & Standardize Exam Scores
                        </div>
                        <div className="question body-b3">
                          Have you appeared for any other test (like GRE, GMAT and SAT)?
                        </div>
                        <div className="anster-button">
                          <Row gutter={[16, 16]} justify="center">
                            <Col xl={12} md={24} sm={24} xs={24} style={{ maxHeight: "20px" }}>
                              <Form.Item>
                                <Checkbox
                                  value={true}
                                  checked={standardizedExamTaken === true}
                                  onChange={() => setStandardizedExamTaken(true)}
                                  disabled={isFormDisabled}
                                >
                                  Yes
                                </Checkbox>
                              </Form.Item>
                            </Col>
                            <Col xl={12} md={24} sm={24} xs={24} style={{ maxHeight: "20px" }}>
                              <Form.Item>
                                <Checkbox
                                  value={false}
                                  checked={standardizedExamTaken === false}
                                  onChange={() => setStandardizedExamTaken(false)}
                                  disabled={isFormDisabled}
                                >
                                  None
                                </Checkbox>
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      </CardComponent>
                    </div>
                  </Col>
                </Row>
              </div>

              {!isFormDisabled && (
                <Row justify="center">
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <ButtonComponent
                      label="Save Advance Profile Information"
                      varient="primary"
                      htmlType="submit"
                      className="w-100"
                    />
                  </Col>
                </Row>
              )}
            </Form>
            {isFormDisabled && (
              <Row justify="center">
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <ButtonComponent
                    label="Edit Advance Profile Information"
                    varient="primary"
                    htmlType="button"
                    onClick={() => setIsFormDisabled(false)}
                    className="w-100"
                  />
                </Col>
              </Row>
            )}
          </CardComponent>
        </Container>
      </div>
    </Fragment>
  );
};

export default AdvaceProfileInformation;
